/**
 * The frame (1.I-1 deliverable 5): a "Skip to page" link, the sidebar, the header and a content
 * region holding the router's `Outlet` (passed as children). The frame renders no `main`: each
 * page renders its own. The sidebar is this package's own markup on the kit's classes and
 * `SeparatorHandle`, with Types and the destination rows in stage s1.
 */
import { Icon, IconButton, isApplePlatform, SeparatorHandle } from '@codex/ui';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AppSlotOutlet } from '../editor-services/slots.js';
import { CaptureHost } from '../inbox/capture-host.js';
import { NowChip } from '../when/now-chip.js';
import { useCreatePage } from './create.js';
import { ShellHeader } from './header.js';
import { HeaderSlotContext } from './header-slot.js';
import { isSidebarToggle } from './keys.js';
import {
  clampWidth,
  readPrefs,
  type ShellPrefs,
  SIDEBAR_WIDTH,
  writePrefs,
} from './layout-prefs.js';
import { useLiveRefresh } from './live.js';
import { useGoTo } from './open-doc.js';
import { PinsSection } from './pins.js';
import { RecentSection } from './recent.js';
import { DestinationRows, InboxRow, ReferenceSection, TypesSection } from './sidebar-types.js';
import { StoryTree } from './tree/story-tree.js';

const SIDEBAR_ID = 'cx-sidebar';

/** Moves focus to the page's own `main` (the frame renders none), made focusable on demand. */
export function focusMain(): void {
  const main = document.querySelector('main');
  if (!main) return;
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
  main.focus();
}

export function Frame({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<ShellPrefs>(() => readPrefs());
  const [headerEnd, setHeaderEnd] = useState<HTMLElement | null>(null);
  const prefsRef = useRef(prefs);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const createPage = useCreatePage();
  const goTo = useGoTo();
  const apple = isApplePlatform();
  useLiveRefresh();

  const update = useCallback((patch: Partial<ShellPrefs>) => {
    const next = { ...prefsRef.current, ...patch };
    prefsRef.current = next;
    setPrefs(next);
    writePrefs(next);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isSidebarToggle(event, apple)) return;
      event.preventDefault();
      const collapsing = !prefsRef.current.collapsed;
      // Focus leaves a sidebar that is about to lose its rows before it does.
      if (collapsing && sidebarRef.current?.contains(document.activeElement)) focusMain();
      update({ collapsed: collapsing });
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [apple, update]);

  const collapsed = prefs.collapsed;
  const width = collapsed ? SIDEBAR_WIDTH.rail : prefs.width;
  const shortcut = apple ? '⌘\\' : 'Ctrl+\\';

  return (
    <div className="cx-frame">
      {/* biome-ignore lint/a11y/useValidAnchor: a skip link moves focus to the page's own main */}
      <a
        className="cx-skip-link"
        href="#cx-page"
        onClick={(event) => {
          event.preventDefault();
          focusMain();
        }}
      >
        Skip to page
      </a>
      <nav
        id={SIDEBAR_ID}
        ref={sidebarRef}
        className="cx-sidebar"
        aria-label="Sidebar"
        data-testid="shell.sidebar.root"
        data-collapsed={collapsed ? '' : undefined}
        style={{ width, minWidth: width }}
      >
        <div className="cx-sidebar__top">
          <a
            className="cx-sidebar__brand cx-shell-link"
            href="/"
            onClick={(event) => goTo('/', event)}
          >
            <Icon name="book" />
            <span className="cx-sidebar__brand-text">Campaign Codex</span>
          </a>
          <IconButton
            icon="sidebar"
            label={`${collapsed ? 'Expand' : 'Collapse'} sidebar (${shortcut})`}
            onClick={() => update({ collapsed: !collapsed })}
            className="cx-sidebar__toggle"
          />
          <IconButton icon="plus" label="New page" onClick={() => void createPage()} />
        </div>
        {!collapsed && (
          <div className="cx-sidebar__scroll">
            <AppSlotOutlet slot="shell.top" />
            <InboxRow />
            <PinsSection />
            <section className="cx-nav-section" aria-label="Story">
              <div className="cx-nav-section__head">
                <h2 className="cx-nav-section__title">Story</h2>
              </div>
              <StoryTree
                expanded={prefs.expanded}
                onExpandedChange={(expanded) => update({ expanded })}
              />
            </section>
            <TypesSection />
            <ReferenceSection />
            <DestinationRows title="Destinations" keys={['types', 'chronicle', 'secrets']} />
            <RecentSection />
            <DestinationRows title="Workspace" keys={['trash', 'settings']} />
            <AppSlotOutlet slot="shell.workspace" />
          </div>
        )}
        {!collapsed && (
          <SeparatorHandle
            label="Resize sidebar"
            value={prefs.width}
            min={SIDEBAR_WIDTH.min}
            max={SIDEBAR_WIDTH.max}
            onChange={(value) => update({ width: clampWidth(value) })}
            controls={SIDEBAR_ID}
            className="cx-sidebar__handle"
          />
        )}
      </nav>
      <div className="cx-frame__column">
        <HeaderSlotContext.Provider value={headerEnd}>
          <ShellHeader endRef={setHeaderEnd} />
          <NowChip />
          <div className="cx-frame__content" id="cx-page">
            {children}
          </div>
        </HeaderSlotContext.Provider>
      </div>
      <CaptureHost />
    </div>
  );
}
